import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Paper,
  Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography
} from '@mui/material';
import { Add, Delete, Edit, People } from '@mui/icons-material';
import '../../services/GlobalVariables';

const emptyForm = {
  id: null,
  name: '',
  fatherName: '',
  phone: '',
  designation: '',
  status: 'Active'
};

const getApiError = (error, fallback) => {
  const message = error.response?.data?.message;
  const detail = error.response?.data?.error;
  if (message && detail) return `${message}: ${detail}`;
  return message || error.message || fallback;
};

export default function EmployeeManagement() {
  const { user } = useSelector((state) => state.auth);
  const cmpID = user?.cmpID || 1;
  const branchID = user?.defaultBranchID || user?.branchID || null;
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${global.SET_API_URL}employees`, {
        params: { cmpID }
      });
      setEmployees(response.data.employees || []);
    } catch (requestError) {
      setError(getApiError(requestError, 'Employees could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [cmpID]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const summary = useMemo(() => ({
    total: employees.length,
    active: employees.filter((employee) => employee.status === 'Active').length,
    inactive: employees.filter((employee) => employee.status === 'Inactive').length
  }), [employees]);

  const openDialog = (employee = null) => {
    setForm(employee ? {
      id: employee.id,
      name: employee.name || '',
      fatherName: employee.fatherName || '',
      phone: employee.phone || '',
      designation: employee.designation || '',
      status: employee.status || 'Active'
    } : emptyForm);
    setDialogOpen(true);
  };

  const saveEmployee = async () => {
    if (!form.name.trim()) {
      setError('Employee name is required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${global.SET_API_URL}employees`, {
        ...form,
        cmpID,
        branchID
      });
      setMessage(response.data.message);
      setDialogOpen(false);
      setForm(emptyForm);
      await loadEmployees();
    } catch (requestError) {
      setError(getApiError(requestError, 'Employee could not be saved.'));
      setLoading(false);
    }
  };

  const deleteEmployee = async (employee) => {
    if (!window.confirm(`Delete employee "${employee.name}"?`)) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.delete(`${global.SET_API_URL}employees/${employee.id}`, {
        params: { cmpID }
      });
      setMessage(response.data.message);
      await loadEmployees();
    } catch (requestError) {
      setError(getApiError(requestError, 'Employee could not be deleted.'));
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: 2,
        justifyContent: 'space-between', alignItems: 'center', mb: 3
      }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Employees</Typography>
          <Typography color="text.secondary">
            Company employees ki entry aur status yahan manage karein.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>
          Add Employee
        </Button>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip icon={<People />} label={`Total: ${summary.total}`} />
        <Chip color="success" label={`Active: ${summary.active}`} />
        <Chip label={`Inactive: ${summary.inactive}`} />
      </Stack>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Employee Name</strong></TableCell>
              <TableCell><strong>Father Name</strong></TableCell>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell><strong>Designation</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center"><CircularProgress size={28} /></TableCell>
              </TableRow>
            ) : employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No employees added yet.</TableCell>
              </TableRow>
            ) : employees.map((employee) => (
              <TableRow key={employee.id} hover>
                <TableCell>{employee.name}</TableCell>
                <TableCell>{employee.fatherName || '-'}</TableCell>
                <TableCell>{employee.phone || '-'}</TableCell>
                <TableCell>{employee.designation || '-'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={employee.status === 'Active' ? 'success' : 'default'}
                    label={employee.status}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<Edit />} onClick={() => openDialog(employee)}>
                    Edit
                  </Button>
                  <Button
                    size="small" color="error" startIcon={<Delete />}
                    onClick={() => deleteEmployee(employee)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form.id ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Stack spacing={2}>
            <TextField
              autoFocus required label="Employee Name" value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Father Name" value={form.fatherName}
              onChange={(event) => setForm({ ...form, fatherName: event.target.value })}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Phone" value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              inputProps={{ maxLength: 30 }}
            />
            <TextField
              label="Designation" value={form.designation}
              onChange={(event) => setForm({ ...form, designation: event.target.value })}
              inputProps={{ maxLength: 60 }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status" value={form.status}
                onChange={(event) => setForm({ ...form, status: event.target.value })}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEmployee} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
